import React, { useState, useEffect } from 'react';
import { Instagram, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getInstagramCredentials } from '../lib/apiCredentials';
import { exchangeForLongLivedToken } from '../lib/metaTokens';
import toast from 'react-hot-toast';

interface InstagramConnectProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  hasConnectedAccounts?: boolean;
}

interface InstagramAccount {
  id: string;
  name: string;
  access_token: string;
  type: 'instagram';
}

export function InstagramConnect({ onSuccess, onError, hasConnectedAccounts }: InstagramConnectProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<InstagramAccount[]>([]);
  const [availableAccounts, setAvailableAccounts] = useState<InstagramAccount[]>([]);
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [connectedAccountIds, setConnectedAccountIds] = useState<string[]>([]);

  useEffect(() => {
    loadConnectedAccounts();

    // Initialize Facebook SDK
    const loadFacebookSDK = async () => {
      const { appId } = await getInstagramCredentials();
      if (!appId) {
        console.error('Instagram App ID not found');
        return;
      }

      // Remove existing SDK if present
      const existingScript = document.getElementById('facebook-jssdk');
      if (existingScript) {
        existingScript.remove();
      }

      // Create new script element
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      
      script.onload = () => {
        const FB = (window as any).FB;
        if (!FB) {
          console.error('Facebook SDK not loaded properly');
          return;
        }

        FB.init({
          appId: appId,
          cookie: true,
          xfbml: true,
          version: 'v18.0'
        });
      };

      document.body.appendChild(script);
    };

    loadFacebookSDK();

    return () => {
      const existingScript = document.getElementById('facebook-jssdk');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const loadConnectedAccounts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('Not authenticated');
        return;
      }

      const { data, error } = await supabase
        .from('connected_accounts')
        .select('id')
        .eq('platform', 'instagram')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error loading connected accounts:', error);
        return;
      }

      setConnectedAccountIds(data?.map(account => account.id) || []);
    } catch (error) {
      console.error('Error loading connected accounts:', error);
    }
  };

  const handleInstagramLogin = async () => {
    setIsLoading(true);
    try {
      const FB = (window as any).FB;
      if (!FB) {
        throw new Error('Facebook SDK not loaded');
      }

      // Reset login state
      await new Promise<void>((resolve) => {
        FB.getLoginStatus((response: any) => {
          if (response.status === 'connected') {
            FB.logout(() => resolve());
          } else {
            resolve();
          }
        });
      });

      // Request permissions
      const permissions = 'instagram_basic,instagram_content_publish,pages_show_list,pages_read_engagement,pages_read_user_content,pages_manage_ads,ads_management,instagram_manage_insights';
      console.log('Requesting Instagram/Facebook permissions:', permissions);

      const response: any = await new Promise((resolve, reject) => {
        FB.login((response: any) => {
          if (response.authResponse) {
            console.log('Instagram login successful, authResponse:', {
              userID: response.authResponse.userID,
              grantedScopes: response.authResponse.grantedScopes
            });
            resolve(response.authResponse);
          } else {
            console.warn('Instagram login cancelled or failed:', response);
            reject(new Error('Facebook login cancelled'));
          }
        }, {
          scope: permissions,
          auth_type: 'rerequest',
          return_scopes: true
        });
      });

      // Get long-lived access token - wymiana po stronie serwera
      console.log('Exchanging for long-lived token...');
      const longLivedToken = await exchangeForLongLivedToken(response.accessToken, 'instagram');
      console.log('Long-lived token obtained successfully');

      // Get Instagram accounts
      const accounts: InstagramAccount[] = [];

      // Get all Facebook pages with pagination
      let allPages: any[] = [];
      let nextUrl: string | null = null;

      do {
        const pagesResponse: any = await new Promise((resolve, reject) => {
          const params = nextUrl ? nextUrl : '/me/accounts?limit=100';
          FB.api(params, { access_token: longLivedToken }, (response: any) => {
            if (response.error) {
              console.error('Facebook API error (Instagram):', response.error);
              reject(response.error);
            } else {
              resolve(response);
            }
          });
        });

        console.log('Facebook pages API response (Instagram):', pagesResponse);

        if (pagesResponse.data && Array.isArray(pagesResponse.data)) {
          allPages = allPages.concat(pagesResponse.data);
          console.log(`Loaded ${pagesResponse.data.length} Facebook pages for Instagram, total: ${allPages.length}`);
        } else {
          console.warn('No data in pages response (Instagram):', pagesResponse);
        }

        nextUrl = pagesResponse.paging?.next || null;
      } while (nextUrl);

      console.log(`Total Facebook pages to check for Instagram: ${allPages.length}`);
      console.log(`Already connected Instagram IDs:`, connectedAccountIds);

      if (!allPages.length) {
        throw new Error('No Facebook pages found');
      }

      // For each page, check if it has an Instagram business account
      for (const page of allPages) {
        try {
          const instagramResponse: any = await new Promise((resolve, reject) => {
            FB.api(
              `/${page.id}?fields=instagram_business_account{id,name,username}`,
              { access_token: page.access_token },
              (response: any) => {
                if (response.error) {
                  reject(response.error);
                } else {
                  resolve(response);
                }
              }
            );
          });

          if (instagramResponse.instagram_business_account) {
            const instagramAccount = {
              id: instagramResponse.instagram_business_account.id,
              name: instagramResponse.instagram_business_account.name || 
                    instagramResponse.instagram_business_account.username || 
                    `Instagram for ${page.name}`,
              access_token: page.access_token,
              type: 'instagram' as const
            };

            // Only add if not already connected
            const isConnected = connectedAccountIds.includes(instagramAccount.id);
            if (isConnected) {
              console.log(`Instagram account "${instagramAccount.name}" (${instagramAccount.id}) is already connected`);
            } else {
              console.log(`Found new Instagram account: "${instagramAccount.name}" (${instagramAccount.id})`);
              accounts.push(instagramAccount);
            }
          }
        } catch (error) {
          console.error(`Error fetching Instagram account for page ${page.name}:`, error);
        }
      }

      console.log(`Total Instagram accounts found: ${accounts.length} new accounts to connect`);
      setAvailableAccounts(accounts);
      
      if (accounts.length === 0) {
        const checkedPagesCount = allPages.length;
        const connectedCount = connectedAccountIds.length;

        if (connectedCount > 0 && checkedPagesCount > 0) {
          toast.error(`All Instagram accounts from your ${checkedPagesCount} Facebook pages are already connected (${connectedCount} total).`);
        } else if (checkedPagesCount > 0) {
          toast.error(`Checked ${checkedPagesCount} Facebook pages but found no Instagram Business accounts. Make sure your pages have Instagram business accounts connected.`);
        } else {
          toast.error('No Facebook pages found. Please create a Facebook page and connect it to an Instagram business account.');
        }
        setShowAccountSelector(false);
      } else {
        toast.success(`Found ${accounts.length} new Instagram account(s) to connect!`);
        setShowAccountSelector(true);
        setSelectedAccounts(accounts);
      }
    } catch (err) {
      console.error('Instagram login error:', err);
      onError?.(err instanceof Error ? err.message : 'Failed to connect Instagram account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccountSelection = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Please select at least one account');
      return;
    }

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Save each selected account
      for (const account of selectedAccounts) {
        // Check if account is already connected to a different user
        const { data: existingAccount } = await supabase
          .from('connected_accounts')
          .select('user_id, name')
          .eq('id', account.id)
          .eq('platform', 'instagram')
          .maybeSingle();

        if (existingAccount) {
          if (existingAccount.user_id === user.id) {
            console.log(`Account ${account.name} is already connected to current user`);
            toast.success(`${account.name} jest już podłączony do Twojego konta`);
            continue;
          } else {
            // Account belongs to different user - delete old connection and create new one
            console.log(`Transferring account ${account.name} from another user to current user`);

            // Delete old connection
            const { error: deleteError } = await supabase
              .from('connected_accounts')
              .delete()
              .eq('id', account.id)
              .eq('platform', 'instagram');

            if (deleteError) {
              console.error('Error deleting old connection:', deleteError);
              throw deleteError;
            }
          }
        }

        // Insert new connection (or insert after deletion)
        const { error: dbError } = await supabase
          .from('connected_accounts')
          .insert({
            id: account.id,
            name: account.name,
            platform: 'instagram',
            account_type: account.type,
            access_token: account.access_token,
            user_id: user.id,
          });

        if (dbError) {
          console.error('Error inserting account:', dbError);
          throw dbError;
        }

        if (existingAccount && existingAccount.user_id !== user.id) {
          toast.success(`${account.name} został przeniesiony z innego konta`);
        }
      }

      setShowAccountSelector(false);
      onSuccess?.();
      toast.success('Instagram accounts connected successfully');
    } catch (error) {
      console.error('Error saving accounts:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to save Instagram accounts');
    } finally {
      setIsLoading(false);
    }
  };

  if (showAccountSelector) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Select Instagram Accounts</h3>
        <p className="text-sm text-gray-500">Select the accounts you want to manage</p>
        
        <div className="max-h-60 overflow-y-auto">
          {availableAccounts.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <p className="text-sm text-yellow-700">
                No new Instagram accounts found. Make sure your Facebook page has an Instagram business account connected.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {availableAccounts.map((account) => (
                <label key={account.id} className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedAccounts.some(a => a.id === account.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAccounts([...selectedAccounts, account]);
                      } else {
                        setSelectedAccounts(selectedAccounts.filter(a => a.id !== account.id));
                      }
                    }}
                    className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-900">{account.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={() => setShowAccountSelector(false)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
          >
            Cancel
          </button>
          <button
            onClick={handleAccountSelection}
            disabled={isLoading || selectedAccounts.length === 0}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Connecting...' : 'Connect Selected Accounts'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={handleInstagramLogin}
      disabled={isLoading}
      className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg 
        text-gray-500 hover:text-pink-600 hover:border-pink-600 w-full justify-center
        disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isLoading ? (
        <RefreshCw className="w-5 h-5 animate-spin" />
      ) : (
        <>
          <Instagram className="h-5 w-5" />
          <span>{hasConnectedAccounts ? 'Connect Another Instagram Account' : 'Connect Instagram'}</span>
        </>
      )}
    </button>
  );
}